import { useLoaderData } from 'react-router';

import type { loader } from '~/routes/_app.project.$urlSegment._index';
import { getDetailsItems } from '@modules/project/component/details/util/get-details-items';
import type { ProjectSpecItem } from '../sections/AboutProjectRowContent';

/**
 * What About the Project says: the editorial lead statement, the description,
 * and the spec table under them. Read off the loader by the section that draws
 * it, and by nothing else — it used to be one of the accordion's rows.
 */
export const useAboutProject = (): {
    heading?: string;
    contentHtml?: string;
    specItems: ProjectSpecItem[];
    hasAbout: boolean;
} => {
    const { project } = useLoaderData<typeof loader>();
    // Drops the boilerplate "ArchiPro Project Summary -" prefix some records
    // carry, as the Story section does.
    const heading = (project.Subheading ?? '')
        .replace(/^\s*ArchiPro Project Summary\s*[-–—:]\s*/i, '')
        .trim();
    const contentHtml = (project.Content ?? '').trim();

    // The project spec table (Title / Builder / Category / Completed / …) — the
    // same rows the legacy details list builds, folded in under the description
    // (mirrors the product page's (01) Product Details table). Title, the
    // professional, Category and Photographers all come off the table: the
    // title is the page's own heading, the category trail is in the
    // breadcrumbs, and both the professional and the photographers are credited
    // in the Project team column at the top of the page. Awards get their own
    // moment elsewhere. The professional's row is matched on its value rather
    // than its label, because that label is the role — Architect here, Builder
    // or Designer on another project.
    const professionalTitle = project.Professional?.Title;
    const specItems = getDetailsItems(project, {
        includeNewFields: true,
    }).filter(
        (item) =>
            item.itemName &&
            item.itemName !== 'Awards' &&
            item.itemName !== 'Title' &&
            item.itemName !== 'Category' &&
            item.itemName !== 'Photographers' &&
            !(professionalTitle && item.value === professionalTitle),
    );

    return {
        heading: heading || undefined,
        contentHtml: contentHtml || undefined,
        specItems,
        hasAbout: Boolean(heading || contentHtml || specItems.length > 0),
    };
};
